import React from 'react';
import { Button, Space, Modal, Form, Input, Card, Select } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Service } from '@/apis';
import type { CategoryAddDto, CategoryUpdateDto } from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { useFormModal } from '@/hooks/useFormModal';
import { SearchBar } from '@/components/SearchBar';
import {
  createTimeColumn,
  updateTimeColumn,
  createLongTextColumn,
} from '@/utils/commonUtils';
import { getDefaultIconNames } from '@/utils/iconRenderer';
import { renderIcon } from '@/utils/iconRenderer';
import { MultiImageUpload } from '@/page/Cms/ProductManagement/productShared';
import styles from '@/components/CommonTable/index.module.less';

const singleImageValueProps = {
  getValueFromEvent: (urls: string[]) => urls?.[0] ?? '',
  getValueProps: (v: string) => ({ value: v ? [v] : [] }),
};

const DEFAULT_ICON_OPTIONS = getDefaultIconNames().map(name => ({
  value: name,
  label: name,
}));

type CategoryRow = Record<string, unknown> & {
  id: number;
  key?: string;
  title?: string;
  description?: string;
  image?: string;
  subtitle?: string;
  highlights?: string[];
  icon?: string;
  path?: string;
  sortOrder?: number;
};

const normalizeRows = (rows: unknown[]): CategoryRow[] => {
  return (rows || []).map((r: unknown) => {
    const item = r as Record<string, unknown>;
    const data = (item.data as Record<string, unknown>) || item;
    return { ...item, ...data, id: (data.id as number) ?? (item.id as number) } as CategoryRow;
  });
};

const CategoryManagement: React.FC = () => {
  const fetchApi = async (pn: number, rn: number, custom?: string) => {
    const res = await Service.ycCategoryControllerGet(pn, rn, custom);
    const rows = (res as { data?: { rows?: unknown[]; count?: number; pn?: number } })?.data?.rows ?? [];
    return {
      errNo: (res as { errNo?: number }).errNo ?? 0,
      data: {
        rows: normalizeRows(rows),
        count: (res as { data?: { count?: number } }).data?.count ?? 0,
        pn: (res as { data?: { pn?: number } }).data?.pn ?? pn,
      },
    };
  };

  const {
    data: list,
    loading,
    pagination,
    fetchData,
    handleDelete,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<CategoryRow>({
    fetchData: fetchApi,
    deleteData: async (id) => { await Service.ycCategoryControllerDelete(id); },
    errorMessage: '获取分类列表失败',
    searchOptions: { fields: ['key', 'title', 'subtitle', 'description', 'path'] },
  });

  const {
    visible,
    editingItem,
    form,
    handleAdd,
    handleEdit: openEdit,
    handleOk,
    handleCancel,
  } = useFormModal<CategoryRow>({
    createData: async (values: CategoryAddDto) => {
      const key = values.key ?? '';
      await Service.ycCategoryControllerAdd({
        ...values,
        path: `/products?category=${key}`,
        image: values.image ?? '',
        subtitle: values.subtitle ?? '',
        highlights: (values.highlights ?? []).filter((s) => typeof s === 'string' && s.trim()),
      });
    },
    updateData: async (values: CategoryUpdateDto) => {
      await Service.ycCategoryControllerPut({
        ...values,
        path: `/products?category=${values.key ?? ''}`,
        image: values.image ?? '',
        subtitle: values.subtitle ?? '',
        highlights: (values.highlights ?? []).filter((s) => typeof s === 'string' && s.trim()),
      });
    },
  });

  const handleEdit = (record: CategoryRow) => {
    const highlights = Array.isArray(record.highlights)
      ? record.highlights.filter((item) => typeof item === 'string')
      : [];
    openEdit({ ...record, highlights, image: record.image ?? '', subtitle: record.subtitle ?? '' });
  };

  const watchedIcon = Form.useWatch('icon', form);
  const watchedKey = Form.useWatch('key', form);
  const watchedPath = Form.useWatch('path', form);
  const pathDisplay = editingItem != null ? (watchedPath ?? editingItem?.path ?? '') : `/products?category=${watchedKey ?? ''}`;
  const onModalOk = async () => {
    const ok = await handleOk();
    if (ok) fetchData();
  };

  const columns: ColumnsType<CategoryRow> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, fixed: 'left' },
    { ...createLongTextColumn('分类 Key', 'key', 12), fixed: 'left' },
    createLongTextColumn('标题', 'title', 12),
    createLongTextColumn('副标题', 'subtitle', 12),
    createLongTextColumn('描述', 'description', 15),
    createLongTextColumn('图标', 'icon', 8),
    createLongTextColumn('路径', 'path', 12),
    { title: '排序', dataIndex: 'sortOrder', key: 'sortOrder', width: 80 },
    createTimeColumn('full'),
    updateTimeColumn('full'),
  ];

  const stats: DataStat[] = [
    { title: '分类总数', value: pagination.total, prefix: <AppstoreOutlined />, valueStyle: { color: '#3f8600' } },
    { title: '当前页', value: list.length, valueStyle: { color: '#1890ff' } },
  ];

  const actionButtons: ActionButton<CategoryRow>[] = [
    { key: 'edit', label: '编辑', icon: <EditOutlined />, onClick: handleEdit },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (record) => handleDelete(record.id),
      confirm: { title: '确定删除该分类？', okText: '确定', cancelText: '取消' },
    },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        <DataStats stats={stats} className={styles.statsRow} />
        <div className={styles.toolbar}>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增分类
            </Button>
            <SearchBar search={search} placeholder="搜索 key、标题、描述、路径" width={300} loading={loading} />
          </Space>
        </div>
        <DataTable
          columns={columns}
          dataSource={list}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>
      <Modal title={editingItem ? '编辑分类' : '新增分类'} open={visible} onOk={onModalOk} onCancel={handleCancel} width={640}>
        <Form form={form} layout="vertical" initialValues={{ highlights: [''] }}>
          <Form.Item
            name="key"
            label="分类 Key"
            extra={
              editingItem
                ? '创建后不可修改，产品通过 Key 关联分类。展示名称请改标题。'
                : '创建后不可修改，作为产品和前台筛选的唯一标识，建议用英文，如 solar、hardware'
            }
            rules={[{ required: true, message: '请输入分类 Key' }]}
          >
            <Input placeholder="如 solar、hardware" disabled={!!editingItem} />
          </Form.Item>
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="显示名称" />
          </Form.Item>
          <Form.Item name="subtitle" label="副标题" extra="首页分类卡片标题下方的一行说明">
            <Input placeholder="如 Milling, turning and post-processing" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} placeholder="可选，用于后台检索" />
          </Form.Item>
          <Form.Item
            name="image"
            label="卡片配图"
            extra="首页「上图下文字」分类卡片顶部图片，建议 4:3"
            {...singleImageValueProps}
          >
            <MultiImageUpload maxCount={1} />
          </Form.Item>
          <Form.List name="highlights">
            {(fields, { add, remove }) => (
              <Form.Item label="要点列表" extra="首页分类卡片中的 2～3 条要点，对应设计稿中的项目符号">
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item {...rest} name={name} noStyle>
                      <Input placeholder="如 Tolerances down to ±.020 mm" style={{ width: 420 }} />
                    </Form.Item>
                    <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  </Space>
                ))}
                {fields.length < 5 && (
                  <Button type="dashed" onClick={() => add('')} size="small" icon={<PlusOutlined />}>
                    添加要点
                  </Button>
                )}
              </Form.Item>
            )}
          </Form.List>
          <Form.Item
            name="icon"
            label="图标"
            extra={watchedIcon ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                预览：{renderIcon(watchedIcon)}
              </span>
            ) : null}
          >
            <Select
              allowClear
              showSearch
              placeholder="请选择图标"
              optionFilterProp="label"
              options={DEFAULT_ICON_OPTIONS}
              optionRender={opt => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {renderIcon(opt.value as string)}
                  <span>{opt.label}</span>
                </span>
              )}
            />
          </Form.Item>
          <Form.Item name="path" label="路径">
            <Input disabled value={pathDisplay} />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序" extra="数字越小越靠前">
            <Select
              allowClear
              placeholder="请选择排序"
              options={Array.from({ length: 31 }, (_, i) => ({ value: i, label: i === 0 ? '0（最前）' : String(i) }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManagement;
